import '@ungap/with-resolvers';
import React, { useEffect, useRef, useState, Component, ErrorInfo } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';

import { LoadingRing } from './LoadingRing';
import { LoadingText } from './LoadingText';

import { getPreviewSignedURL, getIndexerSignedURL } from '../utils/Storage';
import { useDebounce } from '../utils/useDebounce';

import useUserStore from '../stores/User';

// Use CRACO webpack configuration to handle the worker
pdfjs.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL}/static/js/pdf.worker.min.js`;
const pdfOptions = { wasmUrl: `${process.env.PUBLIC_URL}/static/js/wasm/` };

// Local error boundary to catch PDF rendering errors before they bubble up
class PDFErrorBoundary extends Component<
    { children: React.ReactNode; onError: (error: Error) => void },
    { hasError: boolean }
> {
    constructor(props: { children: React.ReactNode; onError: (error: Error) => void }) {
        super(props);
        this.state = { hasError: false };
    }

    static getDerivedStateFromError() {
        return { hasError: true };
    }

    componentDidCatch(error: Error, errorInfo: ErrorInfo) {
        console.error('PDF Error Boundary caught:', error);
        this.props.onError(error);
    }

    render() {
        if (this.state.hasError) {
            return null;
        }
        return this.props.children;
    }
}

interface PDFViewerProps {
    fileUrl: string;
    fileName: string;
    fileId: string;
    allowDownload?: boolean;
    /**
     * Supplies a fresh signed URL when the current one expires. Defaults to the
     * logged-in preview/indexer flow; the public Featured Script page passes its
     * own resolver because it has no session to sign with.
     */
    onRefreshUrl?: () => Promise<string | null>;
    /**
     * Runs the caller's own download flow for the toolbar button. Always prefer
     * this over the preview URL: the library view needs the quota check and the
     * featured page needs the separately-logged public download endpoint.
     */
    onDownload?: () => void | Promise<void>;
    /** Shows a spinner on the toolbar download button while onDownload runs. */
    downloading?: boolean;
}

const PDFViewer: React.FC<PDFViewerProps> = ({ fileUrl, fileName, fileId, allowDownload = false, onRefreshUrl, onDownload, downloading = false }) => {
    const userData = useUserStore((state) => state.data);
    const [numPages, setNumPages] = useState<number>(0);
    const [pageNumber, setPageNumber] = useState<number>(1);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [status, setStatus] = useState<string>('Loading PDF...');
    const [containerWidth, setContainerWidth] = useState<number>(0);
    const [pageLoading, setPageLoading] = useState<boolean>(false);
    const [retryKey, setRetryKey] = useState<number>(0);
    const [currentFileUrl, setCurrentFileUrl] = useState<string>(fileUrl);
    const [browserUnsupported, setBrowserUnsupported] = useState<boolean>(false);
    const containerRef = useRef<HTMLDivElement>(null);

    // Debounce the container width to reduce flickering
    const debouncedWidth = useDebounce(containerWidth, 150);

    // Refresh the rendered document when a new signed URL is provided
    useEffect(() => {
        if (!fileUrl) return;
        setCurrentFileUrl(fileUrl);
        setRetryKey(prev => prev + 1);
        setPageNumber(1);
        setLoading(true);
        setError(null);
        setStatus('Loading PDF...');
    }, [fileUrl]);

    // Suppress ResizeObserver loop warnings
    React.useEffect(() => {
        const originalError = console.error;
        console.error = (...args) => {
            if (typeof args[0] === 'string' && args[0].includes('ResizeObserver loop completed')) {
                return;
            }
            originalError.call(console, ...args);
        };

        return () => {
            console.error = originalError;
        };
    }, []);

    function onDocumentLoadSuccess({ numPages }: { numPages: number }) {
        console.log('✓ PDF loaded successfully with', numPages, 'pages');
        setStatus(`✓ PDF loaded successfully with ${numPages} pages`);
        setNumPages(numPages);
        setLoading(false);
        setError(null);
        setBrowserUnsupported(false);
    }

    function handlePDFError(error: Error) {
        console.error('✗ Error loading PDF:', error);

        // Check if it's a browser compatibility issue
        if (error.message && (error.message.includes('Promise.withResolvers') || error.message.includes('Unexpected token'))) {
            setBrowserUnsupported(true);
            setError("Please update your browser to a newer version or download the file instead.");
        } else {
            setStatus(`✗ Error loading PDF: ${error.message}`);
            setError(`Failed to load PDF: ${error.message}`);
        }
        setLoading(false);
    }

    function onDocumentLoadError(error: Error) {
        handlePDFError(error);
    }

    function onDocumentLoadStart() {
        console.log('→ Document load started');
        setStatus('→ Document load started');
        setLoading(true);
    }

    const handleRetry = async () => {
        setError(null);
        setLoading(true);
        setPageNumber(1);
        setStatus('Getting fresh document link...');

        try {
            // Get a new signed URL
            const newSignedUrl = onRefreshUrl ?
                await onRefreshUrl() :
                (userData?.isIndexer || userData?.isApprover || userData?.isCurator) ?
                    await getIndexerSignedURL(fileName, userData?.user_id) :
                    await getPreviewSignedURL(fileName, fileId, userData?.user_id);
            if (newSignedUrl) {
                setCurrentFileUrl(newSignedUrl as string);
                setStatus('Retrying PDF load...');
                // Force re-render of Document component by changing key
                setRetryKey(prev => prev + 1);
            } else {
                throw new Error('Unable to get fresh document link');
            }
        } catch (error) {
            console.error('Error getting fresh signed URL:', error);
            setError('Unable to refresh document link');
            setLoading(false);
        }
    };

    useEffect(() => {
        const updateContainerWidth = () => {
            if (containerRef.current) {
                setContainerWidth(containerRef.current.clientWidth);
            }
        };

        // Initial update
        updateContainerWidth();

        const resizeObserver = new ResizeObserver((entries) => {
            // Wrap in requestAnimationFrame to avoid ResizeObserver loop errors
            window.requestAnimationFrame(() => {
                if (!Array.isArray(entries) || !entries.length) {
                    return;
                }
                updateContainerWidth();
            });
        });

        if (containerRef.current) {
            resizeObserver.observe(containerRef.current);
        }

        return () => resizeObserver.disconnect();
    }, []);

    if (error) {
        return (
            <div className="d-flex flex-column align-items-center justify-content-center p-4 text-center h-100">
                <i className={'bi bi-exclamation-triangle text-warning mb-2 fs-1'}></i>
                <h6 className="mb-2">{browserUnsupported ? 'Browser Version Not Supported' : 'Unable to load PDF'}</h6>
                <p className="text-muted small mb-3">
                    {error}
                </p>
                {browserUnsupported && (
                    <p className="text-muted small mb-3">
                        We recommend the following minimum browser versions:<br />
                        <strong>Chrome/Edge:</strong> 119 or newer, <strong>Firefox:</strong> 121 or newer, <strong>Safari:</strong> 17.4 or newer
                    </p>
                )}
                {!browserUnsupported && (
                    <button
                        className="btn btn-outline-primary btn-sm"
                        onClick={handleRetry}
                    >
                        <i className="bi bi-arrow-clockwise me-1"></i>
                        Try Again
                    </button>
                )}
                {allowDownload && (
                    <button
                        className="btn btn-primary btn-sm mt-2"
                        disabled={downloading}
                        onClick={() => (onDownload ? onDownload() : window.open(fileUrl, '_blank', 'noopener,noreferrer'))}
                    >
                        <i className="bi bi-download me-1"></i>
                        Download File
                    </button>
                )}
            </div>
        );
    }

    return (
        <div className="w-100 h-100 d-flex flex-column">
            {loading && (
                <div className="text-center p-2">
                    <LoadingText text={status} />
                </div>
            )}

            {!loading && numPages > 0 && (
                <div className="text-center py-1 flex-shrink-0 border-bottom">
                    <button
                        disabled={pageNumber <= 1 || pageLoading}
                        onClick={() => {
                            setPageLoading(true);
                            setPageNumber(1);
                        }}
                        className="btn btn-outline-primary btn-sm me-1"
                        style={{ fontSize: '0.7rem', padding: '2px 6px' }}
                    >
                        <i
                            className="bi bi-skip-backward-fill"
                            style={{
                                position: 'relative',
                                top: '1px',
                            }}
                        />
                    </button>
                    <button
                        disabled={pageNumber <= 1 || pageLoading}
                        onClick={() => {
                            setPageLoading(true);
                            setPageNumber(pageNumber - 1);
                        }}
                        className="btn btn-outline-primary btn-sm me-1"
                        style={{ fontSize: '0.7rem', padding: '2px 6px' }}
                    >
                        <i
                            className="bi bi-caret-left-fill"
                            style={{
                                position: 'relative',
                                top: '1px',
                            }}
                        />
                    </button>
                    <span className="mx-1 small">
                        {pageNumber} / {numPages}
                    </span>
                    <button
                        disabled={pageNumber >= numPages || pageLoading}
                        onClick={() => {
                            setPageLoading(true);
                            setPageNumber(pageNumber + 1);
                        }}
                        className="btn btn-outline-primary btn-sm me-1"
                        style={{ fontSize: '0.7rem', padding: '2px 6px' }}
                    >
                        <i
                            className="bi bi-caret-right-fill"
                            style={{
                                position: 'relative',
                                top: '1px',
                            }}
                        />
                    </button>

                    <button
                        disabled={pageNumber >= numPages || pageLoading}
                        onClick={() => {
                            setPageLoading(true);
                            setPageNumber(numPages);
                        }}
                        className="btn btn-outline-primary btn-sm ms-1"
                        style={{ fontSize: '0.7rem', padding: '2px 6px' }}
                    >
                        <i
                            className="bi bi-skip-forward-fill"
                            style={{
                                position: 'relative',
                                top: '1px',
                            }}
                        />
                    </button>

                    {allowDownload && (
                        <button
                            disabled={downloading}
                            onClick={() => (onDownload ? onDownload() : window.open(fileUrl, '_blank', 'noopener,noreferrer'))}
                            className="btn btn-primary btn-sm ms-3"
                            style={{ fontSize: '0.7rem', padding: '2px 8px' }}
                            title={`Download ${fileName}`}
                            aria-label={`Download ${fileName}`}
                        >
                            {downloading ? (
                                <span
                                    className="spinner-border spinner-border-sm"
                                    role="status"
                                    aria-hidden="true"
                                    style={{ width: '0.7rem', height: '0.7rem' }}
                                />
                            ) : (
                                <>
                                    <i
                                        className="bi bi-file-earmark-arrow-down-fill me-1"
                                        style={{ position: 'relative', top: '1px' }}
                                    />
                                    Download
                                </>
                            )}
                        </button>
                    )}
                </div>
            )}

            <div
                ref={containerRef}
                className="flex-grow-1 d-flex justify-content-center align-items-start overflow-auto p-1"
                style={{
                    minHeight: 0,
                    minWidth: 0,
                    WebkitOverflowScrolling: 'touch'
                }}
            >
                {currentFileUrl && (
                    <div className="pdf-wrapper">
                        <button
                            className="pdf-nav-btn left-btn"
                            disabled={pageNumber <= 1 || pageLoading}
                            data-icon="◀"
                            onClick={() => setPageNumber(pageNumber - 1)}>◀</button>
                        <PDFErrorBoundary onError={handlePDFError}>
                            <Document
                                key={retryKey}
                                file={currentFileUrl}
                                options={pdfOptions}
                                onLoadStart={onDocumentLoadStart}
                                onLoadSuccess={onDocumentLoadSuccess}
                                onLoadError={onDocumentLoadError}
                                loading={<LoadingRing />}
                            >
                                {!loading && (
                                    <div
                                        className="position-relative d-inline-block"
                                        style={{
                                            visibility: pageLoading ? 'hidden' : 'visible',
                                            maxWidth: '100%',
                                            overflow: 'hidden'
                                        }}
                                    >
                                        <Page
                                            pageNumber={pageNumber}
                                            width={debouncedWidth > 0 ? Math.min(debouncedWidth - 20, debouncedWidth * 0.96) : undefined}
                                            renderTextLayer={true}
                                            renderAnnotationLayer={true}
                                            onRenderSuccess={() => setPageLoading(false)}
                                        />
                                        {pageLoading && (
                                            <div className="position-absolute top-50 start-50 translate-middle" style={{ zIndex: 10 }}>
                                                <LoadingRing />
                                            </div>
                                        )}
                                    </div>
                                )}
                            </Document>
                        </PDFErrorBoundary>
                        <button
                            className="pdf-nav-btn right-btn"
                            disabled={pageNumber >= numPages || pageLoading}
                            data-icon="▶" 
                            onClick={() => setPageNumber(pageNumber + 1)}>▶</button>
                    </div>
                )}
            </div>

        </div>
    );
};

export default PDFViewer;